import { useNavigate } from "react-router-dom";
import { MapPin, FileText, Clock, StickyNote, ChevronRight, TrendingUp } from "lucide-react";
import { ago, fmtDate, money } from "@/lib/crmApi";
import { StageTag, ProductTag, DieTag, ContactChannels } from "@/components/crm/Tags";

export const LeadCard = ({ lead, idx }) => {
  const nav = useNavigate();
  const c = lead.main_contact;
  const t = `lead-card-${idx}`;
  return (
    <div
      role="link"
      tabIndex={0}
      data-testid={t}
      onClick={() => nav(`/crm/${lead.id}`)}
      onKeyDown={(e) => { if (e.key === "Enter") nav(`/crm/${lead.id}`); }}
      className="group w-full cursor-pointer text-left bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_30px_rgba(15,23,42,0.08)] hover:border-slate-300 transition-[box-shadow,border-color] duration-200"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 data-testid={`${t}-name`} className="text-lg font-semibold text-slate-900 truncate">{lead.customer_name}</h3>
          <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3.5 w-3.5" />{lead.country || "No country"}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StageTag stage={lead.stage} testId={`${t}-stage`} />
          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-600 transition-colors" />
        </div>
      </div>

      {(lead.products?.length > 0 || lead.die) && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {lead.products.map((p) => <ProductTag key={p} name={p} testId={`${t}-product-${p.replace(/\s+/g, "-").toLowerCase()}`} />)}
          {lead.die && <DieTag die={lead.die} testId={`${t}-die`} />}
        </div>
      )}

      {lead.last_note && (
        <div data-testid={`${t}-pickup`} className="mt-3 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-sm text-amber-900 flex gap-2">
          <StickyNote className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
          <span className="line-clamp-2"><b className="font-semibold">Pick up from:</b> {lead.last_note.text}</span>
        </div>
      )}

      {c && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span data-testid={`${t}-contact-name`} className="text-sm font-medium text-slate-800">{c.name || "Unnamed contact"}</span>
          <ContactChannels contact={c} testPrefix={`${t}-mode`} />
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500">
        <span data-testid={`${t}-last-quote`} className="inline-flex items-center gap-1">
          <FileText className="h-3.5 w-3.5" /> Last quote: {lead.last_quote_date ? fmtDate(lead.last_quote_date) : "—"}
        </span>
        {lead.opportunity && (
          <span data-testid={`${t}-opportunity`} className="inline-flex items-center gap-1 font-semibold text-emerald-700">
            <TrendingUp className="h-3.5 w-3.5" /> {money(lead.opportunity.total_value, lead.opportunity.currency)}
            {lead.opportunity.total_qty_kg ? ` · ${Number(lead.opportunity.total_qty_kg).toLocaleString("en-IN")} kg` : ""}
          </span>
        )}
        <span data-testid={`${t}-activity`} className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" /> {ago(lead.last_activity_at)}
        </span>
      </div>
    </div>
  );
};
